import {
  Button as AntButton,
  type ButtonProps as AntButtonProps,
  Tooltip,
  type TooltipProps,
} from 'antd'
import { Fragment, forwardRef, isValidElement, type ReactElement } from 'react'

type AntButtonStrippedProps = Omit<AntButtonProps, 'variant' | 'color' | 'icon' | 'children'>

type IconOnlyButtonProps = {
  icon?: ReactElement
  children?: never
  /**
   * Must be a plain string because it is used as the `aria-label` for icon-only buttons.
   * Use empty string for disabled state if needed.
   */
  label: string
} & AntButtonStrippedProps

type ButtonWithChildrenProps = {
  children: React.ReactNode
  icon?: ReactElement
  label?: TooltipProps['title']
} & AntButtonStrippedProps

export type ButtonProps = IconOnlyButtonProps | ButtonWithChildrenProps

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, label, icon, ...props }, ref) => {
    /*
     * antd hack for intl - <FormattedMessage /> isn't typed as fragment,
     * antd does not wrap children in <span />
     */
    const isProbablyAtypicalComponent = (child: ReactElement): boolean =>
      child && isValidElement(child) && child.type !== Fragment && typeof children === 'object'

    const buttonElement = (
      <AntButton
        ref={ref}
        icon={icon}
        aria-label={typeof label === 'string' && label ? label : undefined}
        {...props}
      >
        {isProbablyAtypicalComponent(children as ReactElement) ? <span>{children}</span> : children}
      </AntButton>
    )

    if (label) {
      return <Tooltip title={label}>{buttonElement}</Tooltip>
    }

    return buttonElement
  }
)
